import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse } from '../types';
import { apiService } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (userData: {
    email: string;
    password: string;
    username?: string;
    full_name?: string;
    start_pro_trial?: boolean;
  }) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: () => Promise<string>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const persistAuth = (response: AuthResponse) => {
  localStorage.setItem('token', response.access_token);
  localStorage.setItem('user', JSON.stringify(response.user));
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);

          // Refresh from /me so flags like email_verified stay current.
          const fresh = await apiService.getCurrentUser();
          setUser(fresh);
          localStorage.setItem('user', JSON.stringify(fresh));
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }

      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    const response: AuthResponse = await apiService.login(username, password);
    persistAuth(response);
    setUser(response.user);
  };

  const register = async (userData: {
    email: string;
    password: string;
    username?: string;
    full_name?: string;
    start_pro_trial?: boolean;
  }): Promise<void> => {
    const response: AuthResponse = await apiService.register(userData);
    persistAuth(response);
    setUser(response.user);
  };

  const verifyEmail = async (token: string): Promise<void> => {
    const response: AuthResponse = await apiService.verifyEmail(token);
    persistAuth(response);
    setUser(response.user);
  };

  const resendVerification = async (): Promise<string> => {
    const result = await apiService.resendVerification();
    return result.message;
  };

  const logout = (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isAuthenticated = !!user;

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    verifyEmail,
    resendVerification,
    logout,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
